/**
 * Every requirement type the model declares, and what can be done to each.
 *
 * The LIBRARY's types are listed too, greyed rather than hidden. They are real,
 * a program's own types are built by combining with them, and their effects are
 * visible everywhere - so hiding them would leave someone looking for the source
 * of a template they can see being rendered.
 *
 * What a row does NOT offer is as considered as what it does. Rename, re-base
 * and remove-an-attribute are all absent: each breaks or orphans the
 * requirements already typed by it, and none of them is a form field. See
 * services/requirement_types.update_requirement_type.
 */

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { MONOSPACE } from '../../theme'
import type { RequirementTypeRow } from '../../schema'

/**
 * Why this row cannot be deleted, or ''.
 *
 * Both reasons are stated rather than the button merely being dead: a disabled
 * control with no explanation reads as a permission problem, and neither of
 * these is one.
 */
export function undeletableBecause(row: RequirementTypeRow): string {
  if (row.in_library) {
    return 'Declared in a shared library, which a program may not change'
  }
  if (row.used_by.length) {
    return `Used by ${row.used_by.length} requirement(s): ${row.used_by.join(', ')}`
  }
  return ''
}

export default function RequirementTypesTable({
  rows,
  allowed,
  reason,
  onEdit,
  onDelete,
}: {
  rows: readonly RequirementTypeRow[]
  allowed: boolean
  reason: string
  onEdit: (row: RequirementTypeRow) => void
  onDelete: (row: RequirementTypeRow) => void
}) {
  return (
    <Paper variant="outlined">
      <Table size="small" aria-label="Requirement types">
        <TableHead>
          <TableRow>
            <TableCell>Type</TableCell>
            <TableCell>Level</TableCell>
            <TableCell>Specializes</TableCell>
            <TableCell>Declares</TableCell>
            <TableCell>Statement template</TableCell>
            <TableCell align="right">Used by</TableCell>
            <TableCell padding="none" />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const blocked = undeletableBecause(row)
            return (
              <TableRow key={row.qualified_name} hover>
                <TableCell>
                  <Typography sx={{ fontFamily: MONOSPACE, fontSize: '0.85rem' }}>
                    {row.name}
                  </Typography>
                  {row.in_library && (
                    <Typography variant="caption" color="text.disabled">
                      Library
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{row.level ?? '—'}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {row.specializes.map((base) => (
                      <Chip key={base} size="small" label={base.split('::').pop()} />
                    ))}
                  </Box>
                </TableCell>
                <TableCell>
                  {row.attributes.map((entry) => entry.name).join(', ') || '—'}
                </TableCell>
                <TableCell>
                  {row.template ? (
                    <Typography
                      variant="body2"
                      // Its own or a base's, and the difference matters enough
                      // to show: a type following a base's template stops doing
                      // so the moment somebody saves an edit to it.
                      color={row.own_template ? 'text.primary' : 'text.secondary'}
                      sx={{ fontStyle: row.own_template ? 'normal' : 'italic' }}
                    >
                      {row.template}
                    </Typography>
                  ) : (
                    '—'
                  )}
                  {row.template && !row.own_template && (
                    <Typography variant="caption" color="text.disabled">
                      inherited
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  {row.used_by.length ? (
                    <Tooltip title={row.used_by.join(', ')}>
                      <span>{row.used_by.length}</span>
                    </Tooltip>
                  ) : (
                    '0'
                  )}
                </TableCell>
                <TableCell padding="none" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip
                    title={
                      row.in_library
                        ? 'Declared in a shared library, which a program may not change'
                        : allowed
                          ? ''
                          : reason
                    }
                    describeChild
                  >
                    <span>
                      <Button
                        size="small"
                        onClick={() => onEdit(row)}
                        disabled={!allowed || row.in_library}
                      >
                        Edit
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title={blocked || (allowed ? 'Delete' : reason)} describeChild>
                    <span>
                      <IconButton
                        size="small"
                        aria-label={`Delete ${row.name}`}
                        onClick={() => onDelete(row)}
                        disabled={!allowed || Boolean(blocked)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Paper>
  )
}
